import { Button } from '@/shared/components';
import { useTranslation } from 'react-i18next';
import type { UserResponse } from '@/shared/api';
import { BULK_ACTIONS, planBulkAction, type BulkActionId } from './bulkUserActions';

interface Props {
  selected: UserResponse[];
  currentUserId?: string;
  running: BulkActionId | null;
  onRun: (action: BulkActionId) => void;
  onClear: () => void;
}

/**
 * Aufbauende Aktionen zuerst, die destruktive räumlich abgesetzt dahinter (§11). Nebeneinander
 * in derselben Reihe steht „Zugang sperren" sonst genau dort, wo eben noch „Zugang freigeben"
 * war — und ein Sammellauf ist der Fall, in dem ein Fehlgriff am teuersten ist.
 */
const BUILDING_ACTIONS: BulkActionId[] = ['resend', 'reactivate'];

/**
 * Sammelleiste der Benutzerliste — erscheint erst mit einer Auswahl.
 *
 * <p>Jede Aktion nennt vorab, auf wie viele der ausgewählten Konten sie überhaupt zutrifft.
 * Ohne diese Zahl klickt man „Einladung erneut senden" auf zwölf Konten und bekommt danach
 * neun Fehlermeldungen, die nichts mit einem Fehler zu tun haben.</p>
 */
export function UsersBulkBar({ selected, currentUserId, running, onRun, onClear }: Props) {
  const { t } = useTranslation('accountRetention');
  if (selected.length === 0) return null;

  return (
    <div
      role="region"
      aria-label="Sammelaktionen"
      className="border-line bg-card-alt shadow-card mb-4 flex flex-wrap items-center gap-3 rounded-lg border p-3"
    >
      <p className="text-ink min-w-0 flex-1 text-sm">
        <span className="font-serif text-lg">{selected.length}</span>{' '}
        {selected.length === 1 ? 'Konto ausgewählt' : 'Konten ausgewählt'}
      </p>

      <div className="flex flex-wrap items-center gap-2">
        {BUILDING_ACTIONS.map((id) => (
          <BulkButton
            key={id}
            id={id}
            selected={selected}
            currentUserId={currentUserId}
            running={running}
            onRun={onRun}
          />
        ))}
        <Button variant="ghost" className="min-h-11" disabled={running !== null} onClick={onClear}>
          Auswahl aufheben
        </Button>
      </div>

      <div className="border-line/70 flex w-full items-center gap-2 border-t pt-3 sm:w-auto sm:border-l sm:border-t-0 sm:pl-4 sm:pt-0">
        <BulkButton
          id="deactivate"
          selected={selected}
          currentUserId={currentUserId}
          running={running}
          onRun={onRun}
        />
      </div>
      <p className="text-ink-soft w-full text-sm">{t('bulkHint')}</p>
    </div>
  );
}

function BulkButton({
  id,
  selected,
  currentUserId,
  running,
  onRun,
}: {
  id: BulkActionId;
  selected: UserResponse[];
  currentUserId?: string;
  running: BulkActionId | null;
  onRun: (action: BulkActionId) => void;
}) {
  const { applicable } = planBulkAction(id, selected, currentUserId);
  const definition = BULK_ACTIONS[id];
  return (
    <Button
      variant={definition.destructive ? 'destructive' : 'ghost'}
      className="min-h-11"
      disabled={applicable.length === 0 || running !== null}
      loading={running === id}
      onClick={() => onRun(id)}
    >
      {definition.label}
      <span className="ml-1.5 tabular-nums opacity-80">
        ({applicable.length} von {selected.length})
      </span>
    </Button>
  );
}
